import rules from "../../../../config/questionnaire-key-rules.json";

/**
 * The one key rule (`config/questionnaire-key-rules.json`), in TypeScript: a
 * question's stableKey, an answer or matrix-row value, a matrix measure id, an
 * asset's stableKey. The backend and the frontend both mint here; Python's
 * twin is `apps/python-api/services/questionnaire_assembly/identity.py`, and
 * `tests/fixtures/questionnaire-identity/key-rules.v1.json` holds both to the
 * same cases.
 *
 * A reader keeps a stored key as it is: older keys may be longer or spelled
 * otherwise, and re-minting one would break every reference to it. Only a key
 * that is absent, or that repeats one before it in its namespace, is minted,
 * and a key a writer supplies must be valid (`isValidKey`).
 */
if (rules.schemaVersion !== "questionnaire-key-rules.v1") {
  throw new Error("Unsupported questionnaire key-rules schema");
}

/** The most characters a key minted or supplied now may have. */
export const KEY_MAX_CHARS: number = rules.maxChars;
const SEPARATOR: string = rules.separator;
const FIRST_SUFFIX: number = rules.firstSuffix;
const OUTSIDE = new RegExp(`[^${rules.characters}]+`, "g");
const EDGE_SEPARATORS = new RegExp(`^${SEPARATOR}+|${SEPARATOR}+$`, "g");
const TRAILING_SEPARATORS = new RegExp(`${SEPARATOR}+$`);

/** Whose default ends a chain whose every candidate slugs to nothing. */
export type KeyKind = keyof typeof rules.defaults;

/**
 * The rule's slug of `value`, or `""` when nothing survives. The trailing
 * separators are dropped after the cap as well as before it, so a key is a
 * fixed point of this function. NFC runs first so a decomposed accent is the
 * same key as the composed letter. Latin letters in the key rule stay.
 */
export function slugKey(value: string): string {
  return value
    .normalize("NFC")
    .toLowerCase()
    .replace(OUTSIDE, SEPARATOR)
    .replace(EDGE_SEPARATORS, "")
    .slice(0, KEY_MAX_CHARS)
    .replace(TRAILING_SEPARATORS, "");
}

/**
 * Whether `value` is a key a writer may supply: a non-empty fixed point of the
 * slug. Readers never apply this to a stored key.
 */
export function isValidKey(value: unknown): value is string {
  return typeof value === "string" && value !== "" && slugKey(value) === value;
}

/** `root` then `tail`, the root cut so the whole stays within the cap. */
export function keyWithTail(root: string, tail: string): string {
  return `${root.slice(0, KEY_MAX_CHARS - tail.length).replace(TRAILING_SEPARATORS, "")}${tail}`;
}

/**
 * Each key a reader keeps, by position, else `null`: a present key is kept as
 * stored, unless it repeats an earlier key of the list, case aside. Only the
 * `null` positions are minted.
 */
export function keptKeys(keys: ReadonlyArray<unknown>): Array<string | null> {
  const seen = new Set<string>();
  return keys.map((key) => {
    const present = typeof key === "string" ? key.trim() : "";
    if (!present || seen.has(present.toLowerCase())) return null;
    seen.add(present.toLowerCase());
    return present;
  });
}

/** A taken set over `keys`: the rule compares keys case-insensitively. */
export function takenKeys(keys: Iterable<unknown>): Set<string> {
  const taken = new Set<string>();
  for (const key of keys) {
    if (typeof key === "string" && key) taken.add(key.toLowerCase());
  }
  return taken;
}

function take(root: string, taken: Set<string>): string {
  let candidate = root;
  for (let suffix = FIRST_SUFFIX; taken.has(candidate); suffix += 1) {
    candidate = keyWithTail(root, `${SEPARATOR}${suffix}`);
  }
  taken.add(candidate);
  return candidate;
}

/**
 * A new key: the first slug of `chain` that is not empty, else the kind's
 * default, suffixed while it is taken. `taken` (lower-cased, `takenKeys`) is
 * mutated, so the caller's order decides which occurrence keeps the root.
 */
export function mintKey(
  chain: ReadonlyArray<string | null | undefined>,
  taken: Set<string>,
  kind: KeyKind,
): string {
  const root =
    chain.map((text) => slugKey(text ?? "")).find(Boolean) ??
    rules.defaults[kind];
  return take(root, taken);
}

/**
 * A new answer or measure value: the label's slug while it is free, else the
 * positional `fallback`'s slug while that is free, else the label's slug
 * (else the fallback's, else `value`) suffixed. `taken` (lower-cased) is
 * mutated.
 */
export function mintAnswerValue(
  label: string,
  fallback: string,
  taken: Set<string>,
): string {
  const slug = slugKey(label);
  const positional = slugKey(fallback);
  for (const candidate of [slug, positional]) {
    if (candidate && !taken.has(candidate)) {
      taken.add(candidate);
      return candidate;
    }
  }
  return take(slug || positional || rules.defaults.value, taken);
}
